import axios from 'axios';

/**
 * Create a configured axios instance with auth token injection and refresh-on-401.
 *
 * @param {Object} options
 * @param {string} options.baseUrl
 * @param {import('./storage').TokenStorage} options.storage
 * @param {() => void} [options.onUnauthorized]
 * @param {number} [options.timeout=30000]
 * @returns {import('axios').AxiosInstance}
 */
export function createApiClient({ baseUrl, storage, onUnauthorized, timeout = 30000 }) {
  const api = axios.create({
    baseURL: baseUrl,
    timeout,
    headers: { 'Content-Type': 'application/json' },
  });

  api.interceptors.request.use(async (config) => {
    const token = await storage.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;
      if (!original || original._retry || (status !== 401 && status !== 403)) {
        return Promise.reject(error);
      }
      original._retry = true;

      const refreshToken = await storage.getRefreshToken();
      if (!refreshToken) {
        await storage.clearTokens();
        onUnauthorized?.();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${baseUrl}/auth/refresh`, { refreshToken });
        await storage.setTokens(data.token, data.refreshToken);
        if (original.headers) {
          original.headers.Authorization = `Bearer ${data.token}`;
        }
        return api(original);
      } catch (refreshError) {
        await storage.clearTokens();
        onUnauthorized?.();
        return Promise.reject(refreshError);
      }
    }
  );

  return api;
}
